/**
 * Topbar — the workspace's single chrome bar (ADR-0038, sidebar retired).
 *
 * Composition (left → right):
 *
 *   1. Brand mark      ("F" square — opens the module launcher /launch)
 *   2. ModuleSwitcher  ("Sales ▾" — mini-launcher dropdown)
 *   3. SectionTabs     (active group's items, icon + label; active tab
 *                       carries a muted pill — no underline, no trough)
 *   4. QuickSearch     (Cmd-K placeholder; AI hooks land later)
 *   5. Inbox           (approval + notification surface)
 *   6. ThemeToggle
 *   7. Avatar          (AccountMenu trigger — relocated from the old rail)
 *
 * Flint (the AI teammate) is deliberately NOT in this bar — the persona
 * entry point is the launcher pin/tile and the module switcher row.
 *
 * Below `md` the tabs move to their own scrollable second row so the bar
 * never crushes; the module switcher covers cross-module navigation (the
 * old off-canvas drawer is gone).
 *
 * @see prototype/explorations/module-launcher.html — companion surface
 * @see docs/adr/0038-unified-topbar.md
 */
import { Link, useRouterState } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { Inbox } from "lucide-react";
import { cn } from "@flint/ui";
import { findActiveGroup, visibleNavGroups, type NavItem } from "../lib/nav-config.ts";
import { useWorkspaceChrome, type ChromeSectionTab } from "../lib/use-workspace-chrome.ts";
import { meQueryOptions } from "../../../lib/session.ts";
import { AccountMenu } from "./account-menu.tsx";
import { BrandSquare } from "./brand-mark.tsx";
import { ModuleSwitcher } from "./module-switcher.tsx";
import { QuickSearch } from "./quick-search.tsx";
import { UserAvatar } from "./user-avatar.tsx";
import { ThemeToggle } from "../theme/index.ts";

export interface TopbarProps {
  modules?: readonly string[];
  permissions?: readonly string[];
}

export function Topbar({ modules = [], permissions = [] }: TopbarProps) {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const hideSectionTabs = useWorkspaceChrome((s) => s.hideSectionTabs);
  const pageTabs = useWorkspaceChrome((s) => s.sectionTabs);
  const isInboxActive = pathname === "/approvals" || pathname.startsWith("/approvals/");

  const groups = visibleNavGroups(modules, permissions);
  const activeGroup = hideSectionTabs || pageTabs ? null : findActiveGroup(groups, pathname);
  const chromeTabs = hideSectionTabs ? null : pageTabs;

  return (
    <header className="shrink-0 bg-paper border-b border-line z-30">
      <div className="h-(--top-h) flex items-center gap-1 px-4">
        {/* Brand mark — opens the module launcher (the app's front door). */}
        <Link to="/launch" aria-label="Flint — all modules" className="shrink-0 rounded-md p-0.5">
          <BrandSquare />
        </Link>

        <ModuleSwitcher modules={modules} permissions={permissions} />

        {/* Section tabs — desktop, inline. Mobile gets the second row below.
            Page-published tabs (chrome store) win over nav-config group tabs. */}
        {activeGroup || chromeTabs ? (
          <>
            <div aria-hidden className="mx-2 hidden h-[22px] w-px shrink-0 bg-line md:block" />
            {chromeTabs ? (
              <PageSectionTabs tabs={chromeTabs} className="hidden min-w-0 md:flex" />
            ) : activeGroup ? (
              <SectionTabs
                items={activeGroup.items}
                pathname={pathname}
                ariaLabel={`${activeGroup.label ?? activeGroup.ariaLabel} navigation`}
                className="hidden min-w-0 md:flex"
              />
            ) : null}
          </>
        ) : null}

        <div className="ml-auto flex shrink-0 items-center gap-1">
          <QuickSearch />

          {/* Flint (the AI teammate) has no topbar shortcut — the persona
              entry lives on the launcher and in the module switcher. */}

          {/* Inbox — approval + notification surface (actions + detail live on the page) */}
          <Link
            to="/approvals"
            className={cn(
              "inline-flex items-center gap-1.5 h-8 px-2.5 rounded-md text-sm font-medium transition-colors",
              isInboxActive
                ? "bg-sidebar-accent text-sidebar-accent-foreground"
                : "text-muted hover:bg-muted/10 hover:text-ink",
            )}
          >
            <Inbox className="size-4 shrink-0" aria-hidden />
            <span className="hidden sm:inline">Inbox</span>
          </Link>

          <div className="w-px h-5 bg-line mx-1" />

          <ThemeToggle />

          <TopbarUser />
        </div>
      </div>

      {/* Section tabs — mobile second row (scrollable). */}
      {chromeTabs ? (
        <PageSectionTabs
          tabs={chromeTabs}
          className="flex border-t border-line px-3 py-1.5 md:hidden"
        />
      ) : activeGroup ? (
        <SectionTabs
          items={activeGroup.items}
          pathname={pathname}
          ariaLabel={`${activeGroup.label ?? activeGroup.ariaLabel} navigation`}
          className="flex border-t border-line px-3 py-1.5 md:hidden"
        />
      ) : null}
    </header>
  );
}

/**
 * SectionTabs — the active group's items as icon + label tabs. Only the
 * active tab gets a muted pill (sky label); the rest are quiet text with a
 * whisper-light hover wash. Derived from the same nav-config the launcher
 * and module switcher read.
 */
function SectionTabs({
  items,
  pathname,
  ariaLabel,
  className,
}: {
  items: NavItem[];
  pathname: string;
  ariaLabel: string;
  className?: string;
}) {
  return (
    <nav
      aria-label={ariaLabel}
      className={cn(
        "items-center gap-0.5 overflow-x-auto",
        "[&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]",
        className,
      )}
    >
      {items.map((item) => (
        <TabItem key={item.external ?? item.to} item={item} pathname={pathname} />
      ))}
    </nav>
  );
}

function TabItem({ item, pathname }: { item: NavItem; pathname: string }) {
  const Icon = item.icon;
  const active =
    item.to === "/" ? pathname === "/" : pathname === item.to || pathname.startsWith(`${item.to}/`);

  if (item.disabled) {
    return (
      <span
        className="flex items-center gap-1.5 rounded-[7px] px-3 py-1.5 text-sm text-muted/40 cursor-not-allowed whitespace-nowrap"
        title={`${item.label} · coming soon`}
      >
        <Icon className="size-4 shrink-0" aria-hidden />
        {item.label}
      </span>
    );
  }

  if (item.external) {
    return (
      <a
        href={item.external}
        target="_blank"
        rel="noreferrer noopener"
        className="flex items-center gap-1.5 rounded-[7px] px-3 py-1.5 text-sm font-medium text-muted hover:text-ink hover:bg-raised transition-colors whitespace-nowrap"
      >
        <Icon className="size-4 shrink-0" aria-hidden />
        {item.label}
      </a>
    );
  }

  return (
    <Link
      to={item.to}
      className={cn(
        "flex items-center gap-1.5 rounded-[7px] px-3 py-1.5 text-sm transition-colors whitespace-nowrap",
        active
          ? "bg-line font-semibold text-sky-700"
          : "font-medium text-muted hover:text-ink hover:bg-raised",
      )}
    >
      <Icon className="size-4 shrink-0" aria-hidden />
      {item.label}
    </Link>
  );
}

/**
 * PageSectionTabs — page-published tabs (chrome store) rendered in the same
 * pill idiom as SectionTabs. Used by pages whose sections are filters on one
 * route (the Inbox's status tabs) rather than sibling routes, so activeness
 * comes from the publisher, not the pathname. The optional `count` renders
 * the red attention pill (pending approvals).
 */
function PageSectionTabs({ tabs, className }: { tabs: ChromeSectionTab[]; className?: string }) {
  return (
    <nav
      aria-label="Page sections"
      className={cn(
        "items-center gap-0.5 overflow-x-auto",
        "[&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]",
        className,
      )}
    >
      {tabs.map((tab) => {
        const Icon = tab.icon;
        return (
          <Link
            key={tab.label}
            to={tab.to}
            search={tab.search ?? {}}
            className={cn(
              "flex items-center gap-1.5 rounded-[7px] px-3 py-1.5 text-sm transition-colors whitespace-nowrap",
              tab.active
                ? "bg-line font-semibold text-sky-700"
                : "font-medium text-muted hover:text-ink hover:bg-raised",
            )}
          >
            <Icon className="size-4 shrink-0" aria-hidden />
            {tab.label}
            {tab.count ? (
              <span className="rounded-full bg-bad px-1.5 py-px text-xs font-bold text-white">
                {tab.count}
              </span>
            ) : null}
          </Link>
        );
      })}
    </nav>
  );
}

/**
 * TopbarUser — avatar chip opening the AccountMenu, relocated from the old
 * sidebar footer. The wrapping <button> keeps the accessible name = the
 * user's full name (UserAvatar carries `aria-label`), which the sign-out
 * flows target.
 */
function TopbarUser() {
  const { data: me } = useQuery(meQueryOptions);
  if (!me?.user) return null;

  return (
    <AccountMenu user={me.user} company={me.company} side="bottom" align="end">
      <button
        type="button"
        className="ml-1 flex items-center justify-center rounded-md transition-colors hover:bg-muted/10"
      >
        <UserAvatar name={me.user.name} src={me.user.avatarUrl} size="sm" />
      </button>
    </AccountMenu>
  );
}
